import { useMemo, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HelpCircle, Search, X } from "lucide-react";

export const faqItems = [
  {
    q: "What is \"dual-use\" technology and why do investors treat it differently?",
    a: "Dual-use refers to technologies with both civilian and defence/security applications — such as advanced sensors, autonomy, AI, space, advanced materials, and cybersecurity. Investors approach them with additional diligence around export controls, end-user restrictions, and LP mandates. The upside is access to defence budgets, sovereign capital, and government grants alongside commercial revenue, which often translates into stronger margins and longer customer contracts.",
  },
  {
    q: "How long does a typical fundraising round take for a deep-tech company?",
    a: "From kick-off to wired funds, expect 4–9 months for a seed or Series A in deep-tech and dual-use — longer than a pure SaaS round because of the technical diligence, regulatory checks, and the smaller pool of qualified investors. Companies that come in investment-ready (clean data room, validated TRL claims, credible go-to-market) compress this materially. We typically advise founders to start conversations 6 months before they need cash in the bank.",
  },
  {
    q: "What is TRL (Technology Readiness Level) and why do investors ask about it?",
    a: "TRL is a 1–9 scale, originally from NASA and now standard across the EU and NATO, that describes how mature a technology is — from basic principles (TRL 1) to a system proven in operational environment (TRL 9). Investors use it as shorthand for risk: capital cost, time-to-revenue, and the gap between today and a commercial product. Misstating TRL is one of the fastest ways to lose investor trust, so we help founders present it accurately and tie it to a credible development plan.",
  },
  {
    q: "What's the difference between dilutive and non-dilutive funding for defence tech?",
    a: "Dilutive funding (VC, angels, strategic investors) buys equity and is best suited to scaling commercial traction. Non-dilutive funding — Horizon Europe, EDF, EIC Accelerator, Innovate UK, NSSIF, national defence innovation funds — covers high-risk R&D without giving up ownership. Most successful deep-tech companies blend both: grants and defence contracts to de-risk technology, equity to commercialise it. Sequencing the two correctly can be the difference between a clean cap table and a stalled round.",
  },
  {
    q: "Do you work with pre-seed and seed deep-tech founders, or only later stage?",
    a: "We work across pre-seed through Series B. Earlier stage work focuses on investment readiness, narrative, and warm investor introductions; later stage work involves more structured process management, syndicate construction, and term-sheet negotiation. We are selective about who we take on — we only engage when we believe we can materially improve the outcome.",
  },
  {
    q: "Which European and UK investors actively back dual-use companies?",
    a: "The active landscape includes specialist funds (NATO Innovation Fund, NSSIF, Project A, Expeditions Fund, MD One, 201 Ventures), generalist deep-tech funds with dual-use mandates (Lakestar, Plural, Atomico, IQ Capital, Octopus Ventures), and corporate venture arms from primes and integrators. Beyond the visible names, sovereign and family-office capital plays a quietly significant role. Our network spans 120+ EU/UK investors with relevant mandates.",
  },
  {
    q: "How does the export-control regime affect fundraising for defence tech?",
    a: "EU dual-use regulation (Regulation 2021/821) and UK export control law restrict who can buy, license, and in some cases invest in defence-relevant technology. This affects investor eligibility, due-diligence timelines, and post-close commercial freedom. Founders who address export classification, end-user controls, and FDI screening early in the process avoid surprises in late-stage diligence — and signal operational maturity to serious investors.",
  },
  {
    q: "What's included in a Kayros engagement — and what isn't?",
    a: "A typical engagement includes investment-readiness diagnostics, narrative and pitch materials, data-room construction, targeted investor sourcing, process management, and term-sheet support. We do not act as a regulated broker-dealer, do not take success fees structured as securities placement, and do not work on engagements outside our deep-tech and dual-use focus. Every engagement is governed by an NDA and tailored to the company's stage and capital strategy.",
  },
];

const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const Highlight = ({ text, query }: { text: string; query: string }) => {
  const trimmed = query.trim();
  if (!trimmed) return <>{text}</>;
  const regex = new RegExp(`(${escapeRegExp(trimmed)})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-primary/20 text-foreground rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const FAQ = () => {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();
  const isSearching = trimmed.length > 0;

  const filtered = useMemo(() => {
    if (!isSearching) return faqItems.map((item, idx) => ({ item, idx }));
    return faqItems
      .map((item, idx) => ({ item, idx }))
      .filter(
        ({ item }) =>
          item.q.toLowerCase().includes(trimmed) ||
          item.a.toLowerCase().includes(trimmed)
      );
  }, [trimmed, isSearching]);

  const openValues = isSearching ? filtered.map(({ idx }) => `item-${idx}`) : undefined;

  return (
    <section id="faq" className="py-24 md:py-32 bg-background">
      <div className="container px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-muted/50 mb-6">
              <HelpCircle className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">Frequently Asked Questions</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 line-glow inline-block">
              Deep-Tech Fundraising, Answered
            </h2>
            <p className="text-muted-foreground text-base md:text-lg mt-4">
              Common questions from founders building dual-use and deep-tech companies in Europe and the UK.
            </p>
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                aria-label="Search FAQ questions"
                className="pl-10 pr-10 h-11"
              />
              {isSearching && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {isSearching && (
              <div className="mt-2 text-xs text-muted-foreground text-right">
                {filtered.length} of {faqItems.length} {filtered.length === 1 ? "question" : "questions"}
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <p className="text-muted-foreground mb-4">
                No questions match &ldquo;{query}&rdquo;. Try a different keyword.
              </p>
              <Button variant="outline" size="sm" onClick={() => setQuery("")}>
                Clear search
              </Button>
            </div>
          ) : isSearching ? (
            <Accordion type="multiple" value={openValues} className="w-full">
              {filtered.map(({ item, idx }) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="border-border">
                  <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-foreground hover:text-primary py-5">
                    <Highlight text={item.q} query={query} />
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">
                    <Highlight text={item.a} query={query} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {filtered.map(({ item, idx }) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="border-border">
                  <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-foreground hover:text-primary py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
